/**
 * Manual "lift and shift" ordering for UNDATED tasks (V2 §4e, register
 * #30 — Roxanne, 12 Aug).
 *
 * Contract agreed with the tester (Chad ↔ Roxanne, WhatsApp 12 Aug):
 * in the Date sort, DATED tasks rank by date and are untouchable;
 * UNDATED tasks sit below them, and only those may be reordered by
 * hand. Because undated can never rise above dated, Roxanne's "warn me
 * if I move it above something due soon" cannot happen by construction
 * — the safest prompt is the one the design makes unnecessary.
 *
 * 15 Oct 2026 (founder: "yes" to same-date ordering): every open task on
 * Chad's board carried a date (MEASURED: 51 of 51), so the undated-only rule
 * meant Move up / Move down never appeared at all. Tasks due the SAME DAY now
 * form a group too. Dates still decide everything across days — a task can
 * never be hand-moved above an earlier day — so Roxanne's contract holds:
 * hand order only ever breaks ties the calendar leaves open.
 *
 * Groups (orderGroupKey), matching sortTasks' date buckets exactly:
 *   - a timestamp (deadlineAt)  → its LOCAL calendar day
 *   - a rough date only (hint)  → one "rough date" group
 *   - no date signal at all     → the undated group
 *
 * Mechanism: `task.manualRank` (sparse until first move). On each move
 * the whole undated group is renumbered 0..n then the two neighbours
 * swap — a total, stable order from the first gesture onward. Ranks
 * ride the normal per-task sync (editedAt bump), so a phone reorder
 * appears on the laptop.
 */

import type { Task } from '../types';
import { compareTasks } from '../types';
import { localDateKey } from './affirmation';

export type OrderGroupKind = 'day' | 'rough' | 'none';

/** Which hand-order group an open task belongs to (see header). */
export function orderGroupKey(t: Task): string {
  if (t.deadlineAt !== undefined) return `day:${localDateKey(new Date(t.deadlineAt))}`;
  if (t.deadlineHint) return 'rough';
  return 'none';
}

function groupKind(key: string): OrderGroupKind {
  return key.startsWith('day:') ? 'day' : key === 'rough' ? 'rough' : 'none';
}

/** Any open task can be hand-ordered within its group. */
export function isManuallyOrderable(t: Task): boolean {
  return !t.done;
}

/** The label the card shows above Move up / Move down. */
export function orderGroupLabel(t: Task): string {
  const kind = groupKind(orderGroupKey(t));
  if (kind === 'day') return 'tasks due the same day';
  if (kind === 'rough') return 'tasks with a rough date';
  return 'tasks without a date';
}

/** Display order within a group — MUST match sortTasks' date-mode tie order
 *  (manualRank, then time of day, then canonical). */
export function compareManual(a: Task, b: Task): number {
  const ma = a.manualRank ?? Number.MAX_SAFE_INTEGER;
  const mb = b.manualRank ?? Number.MAX_SAFE_INTEGER;
  if (ma !== mb) return ma - mb;
  const ta = a.deadlineAt ?? 0;
  const tb = b.deadlineAt ?? 0;
  if (ta !== tb) return ta - tb;
  return compareTasks(a, b);
}

function groupOf(zoneTasks: Task[], task: Task): Task[] {
  const key = orderGroupKey(task);
  return zoneTasks.filter((t) => isManuallyOrderable(t) && orderGroupKey(t) === key).sort(compareManual);
}

/**
 * Whether Move up / Move down can act, and — when one cannot — the reason
 * in words the card shows verbatim (CLAUDE.md §2: no state may look like
 * "nothing happened").
 *
 * 12 Oct 2026 (MEASURED in Chad's live board): "Chad: AI test" was the only
 * undated open task in Zone 2 — the other 13 all carried dates — so its
 * group had one member and Move up did nothing at all, silently. The design
 * is right (dates decide across days); the silence was the defect.
 */
export interface MoveAvailability {
  up: boolean;
  down: boolean;
  /** Open tasks in this task's group (same zone, same day / bucket). */
  groupSize: number;
  /** Shown under the buttons when either direction is blocked; null otherwise. */
  reason: string | null;
}

export function moveAvailability(zoneTasks: Task[], taskId: string): MoveAvailability {
  const task = zoneTasks.find((t) => t.id === taskId);
  if (!task || !isManuallyOrderable(task)) return { up: false, down: false, groupSize: 0, reason: null };
  const group = groupOf(zoneTasks, task);
  const i = group.findIndex((t) => t.id === taskId);
  const up = i > 0;
  const down = i < group.length - 1;
  const kind = groupKind(orderGroupKey(task));
  const others = zoneTasks.filter((t) => !t.done && orderGroupKey(t) !== orderGroupKey(task)).length;
  let reason: string | null = null;
  if (group.length === 1) {
    if (kind === 'day') {
      reason =
        'This is the only task due that day in this zone, so there is nothing to reorder it against. Tasks on different days always stay in date order — change its date to move it to another day.';
    } else if (kind === 'rough') {
      reason = 'This is the only task with a rough date in this zone, so there is nothing to reorder it against.';
    } else {
      reason = `This is the only undated task in this zone, so there is nothing to reorder it against.${
        others > 0 ? ' Dated tasks always sit above undated ones, in date order — give this one a date to place it among them.' : ''
      }`;
    }
  } else if (!up) {
    reason =
      kind === 'day'
        ? 'Already the top task for that day in this zone. Earlier days always come first — give it an earlier date to move it higher.'
        : kind === 'rough'
          ? 'Already the top task with a rough date in this zone.'
          : `Already the top undated task in this zone.${
              others > 0 ? ' Dated tasks always sit above undated ones, in date order — give this one a date to place it among them.' : ''
            }`;
  } else if (!down) {
    reason = kind === 'day' ? 'Already the last task for that day in this zone.' : 'Already the last task in this group.';
  }
  return { up, down, groupSize: group.length, reason };
}

/**
 * Compute the rank updates that move `taskId` one step up (-1) or down
 * (+1) among its group (same zone, same day / bucket). Returns updates for
 * the WHOLE group (renumbered), or null when the move is impossible.
 */
export function reorderInGroup(
  zoneTasks: Task[],
  taskId: string,
  direction: -1 | 1,
): Array<{ id: string; manualRank: number }> | null {
  const task = zoneTasks.find((t) => t.id === taskId);
  if (!task || !isManuallyOrderable(task)) return null;
  const group = groupOf(zoneTasks, task);
  const i = group.findIndex((t) => t.id === taskId);
  const j = i + direction;
  if (i < 0 || j < 0 || j >= group.length) return null;
  const order = group.map((t) => t.id);
  [order[i], order[j]] = [order[j], order[i]];
  return order.map((id, rank) => ({ id, manualRank: rank }));
}
